// app/mtd-itsa-web/src/components/application/table/obligations-table.tsx

import { useMemo } from "react";
import { Table, TableCard } from "@/components/application/table/table";
import { cx } from "@/utils/cx";

export type HmrcObligationStatus = "open" | "fulfilled";

export type QuarterlyObligation = {
    project_id: string;
    business_id: string;
    period_start_date: string; // ISO date (YYYY-MM-DD)
    period_end_date: string; // ISO date (YYYY-MM-DD)
    due_date: string; // ISO date
    status: HmrcObligationStatus;
    received_date?: string | null; // ISO date
    type_of_business?: string | null;
    is_deleted?: boolean | null;
};

export type FinalObligation = {
    project_id: string;
    period_start_date: string; // ISO date
    period_end_date: string; // ISO date
    due_date: string; // ISO date
    status: HmrcObligationStatus;
    received_date?: string | null; // ISO date
    is_deleted?: boolean | null;
};

export type HmrcBusinessSummary = {
    business_id: string;
    trading_name?: string | null;
    type_of_business?: string | null;
};

type ObligationsTableProps = {
    /** Businesses to render (one table per business). */
    businesses: HmrcBusinessSummary[];

    /** Quarterly obligations (can include multiple businesses). */
    quarterlyObligations: QuarterlyObligation[];

    /** Final / crystallisation obligations for the project. */
    finalObligations?: FinalObligation[];

    /** Hide soft-deleted rows (recommended for UI). Default: true */
    hideDeleted?: boolean;

    /** Optional wrapper className */
    className?: string;
};

type ColumnDef = { id: string; label: string };

const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" });

function toDate(value?: string | null) {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

function fmtDate(value?: string | null) {
    const d = toDate(value);
    return d ? dateFmt.format(d) : "—";
}

function fmtPeriod(start?: string | null, end?: string | null) {
    const s = fmtDate(start);
    const e = fmtDate(end);
    if (s === "—" && e === "—") return "—";
    return `${s} – ${e}`;
}

function isOverdue(dueDate?: string | null, status?: HmrcObligationStatus) {
    if (status !== "open") return false;
    const due = toDate(dueDate);
    if (!due) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    return due < today;
}

function StatusPill({ status, dueDate }: { status: HmrcObligationStatus; dueDate?: string | null }) {
    const overdue = isOverdue(dueDate, status);

    return (
        <span
            className={cx(
                "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                status === "fulfilled" && "bg-secondary text-primary ring-border-secondary",
                status === "open" && !overdue && "bg-secondary text-primary ring-border-secondary",
                status === "open" && overdue && "bg-error_subtle text-error ring-error_subtle",
            )}
        >
            {status === "fulfilled" ? "Fulfilled" : overdue ? "Overdue" : "Open"}
        </span>
    );
}

function MobileObligationCard({
    title,
    period,
    dueDate,
    status,
    receivedDate,
}: {
    title: string;
    period: string;
    dueDate: string;
    status: HmrcObligationStatus;
    receivedDate: string;
}) {
    return (
        <div className="rounded-lg border border-secondary bg-primary p-4 shadow-xs">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-primary">{title}</div>
                    <div className="mt-0.5 text-xs text-tertiary">{period}</div>
                </div>
                <div className="shrink-0">
                    <StatusPill status={status} />
                </div>
            </div>

            <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
                <div className="min-w-0">
                    <dt className="text-[11px] font-medium text-quaternary">Due date</dt>
                    <dd className="mt-0.5 truncate text-sm text-tertiary">{dueDate}</dd>
                </div>

                <div className="min-w-0">
                    <dt className="text-[11px] font-medium text-quaternary">Received</dt>
                    <dd className="mt-0.5 truncate text-sm text-tertiary">{receivedDate}</dd>
                </div>
            </dl>
        </div>
    );
}

export function ObligationsTable({
    businesses,
    quarterlyObligations,
    finalObligations = [],
    hideDeleted = true,
    className,
}: ObligationsTableProps) {
    const quarterlyColumns: ColumnDef[] = useMemo(
        () => [
            { id: "period", label: "Period" },
            { id: "due", label: "Due date" },
            { id: "status", label: "Status" },
            { id: "received", label: "Received" },
        ],
        [],
    );

    const finalColumns: ColumnDef[] = useMemo(
        () => [
            { id: "period", label: "Tax year period" },
            { id: "due", label: "Due date" },
            { id: "status", label: "Status" },
            { id: "received", label: "Received" },
        ],
        [],
    );

    const quarterlyByBusiness = useMemo(() => {
        const rows = hideDeleted ? quarterlyObligations.filter((o) => !o.is_deleted) : quarterlyObligations;

        return rows.reduce<Record<string, QuarterlyObligation[]>>((acc, o) => {
            (acc[o.business_id] ??= []).push(o);
            return acc;
        }, {});
    }, [quarterlyObligations, hideDeleted]);

    const finalRows = useMemo(() => {
        const rows = hideDeleted ? finalObligations.filter((o) => !o.is_deleted) : finalObligations;
        // stable sort: open first, then due date ascending
        return [...rows].sort((a, b) => {
            return (a.due_date ?? "").localeCompare(b.due_date ?? "");
        });
    }, [finalObligations, hideDeleted]);

    return (
        <div className={cx("flex flex-col gap-6", className)}>


            {/* One table per business */}
            {businesses.map((b) => {
                const rows = [...(quarterlyByBusiness[b.business_id] ?? [])].sort((a, c) => {
                    // open first, then due date ascending
                    return (a.due_date ?? "").localeCompare(c.due_date ?? "");
                });

                const title = b.trading_name?.trim() ? b.trading_name : "Business";
                const subtitleParts = [
                    b.type_of_business?.trim() ? b.type_of_business : null,
                    b.business_id?.trim() ? `Business ID: ${b.business_id}` : null,
                ].filter(Boolean);

                return (
                    <TableCard.Root key={b.business_id}>
                        <TableCard.Header title={title} description={subtitleParts.join(" • ")} badge={rows.length} />

                        {rows.length === 0 ? (
                            <div className="px-4 py-6 text-sm text-tertiary md:px-6 md:py-8">
                                No quarterly obligations found for this business.
                            </div>
                        ) : (
                            <>
                                {/* Mobile: stacked cards */}
                                <div className="px-4 pb-4 md:hidden">
                                    <div className="flex flex-col gap-3">
                                        {rows.map((o) => {
                                            const id = `${o.project_id}:${o.business_id}:${o.period_start_date}:${o.period_end_date}`;
                                            return (
                                                <div key={id}>
                                                    <MobileObligationCard
                                                        title="Quarterly update"
                                                        period={fmtPeriod(o.period_start_date, o.period_end_date)}
                                                        dueDate={fmtDate(o.due_date)}
                                                        status={o.status}
                                                        receivedDate={fmtDate(o.received_date ?? null)}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Desktop: table */}
                                <div className="hidden md:block">
                                    <Table aria-label={`Quarterly obligations for ${title}`}>
                                        <Table.Header columns={quarterlyColumns}>
                                            {(column) => (
                                                <Table.Head
                                                    id={column.id}
                                                    label={column.label}
                                                    isRowHeader={column.id === 'period'} // Add this line
                                                />
                                            )}
                                        </Table.Header>

                                        <Table.Body
                                            items={rows.map((o) => ({
                                                ...o,
                                                id: `${o.project_id}:${o.business_id}:${o.period_start_date}:${o.period_end_date}`,
                                            }))}
                                        >
                                            {(row) => (
                                                <Table.Row id={row.id} columns={quarterlyColumns}>
                                                    {(column) => {
                                                        switch (column.id) {
                                                            case "period":
                                                                return (
                                                                    <Table.Cell>
                                                                        {fmtPeriod(row.period_start_date, row.period_end_date)}
                                                                    </Table.Cell>
                                                                );
                                                            case "due":
                                                                return <Table.Cell>{fmtDate(row.due_date)}</Table.Cell>;
                                                            case "status":
                                                                return (
                                                                    <Table.Cell>
                                                                        <StatusPill status={row.status} dueDate={row.due_date} />
                                                                    </Table.Cell>
                                                                );
                                                            case "received":
                                                                return <Table.Cell>{fmtDate(row.received_date ?? null)}</Table.Cell>;
                                                            default:
                                                                return <Table.Cell>—</Table.Cell>;
                                                        }
                                                    }}
                                                </Table.Row>
                                            )}
                                        </Table.Body>
                                    </Table>
                                </div>
                            </>
                        )}
                    </TableCard.Root>
                );
            })}

            {/* Final / Crystallisation */}
            <TableCard.Root>
                <TableCard.Header
                    title="Final declaration"
                    description="End of tax year obligations for the project."
                    badge={finalRows.length}
                />

                {finalRows.length === 0 ? (
                    <div className="px-4 py-6 text-sm text-tertiary md:px-6 md:py-8">No final obligations found.</div>
                ) : (
                    <>
                        {/* Mobile: stacked cards */}
                        <div className="px-4 pb-4 md:hidden">
                            <div className="flex flex-col gap-3">
                                {finalRows.map((o) => {
                                    const id = `${o.project_id}:${o.period_start_date}:${o.period_end_date}`;
                                    return (
                                        <div key={id}>
                                            <MobileObligationCard
                                                title="Final declaration"
                                                period={fmtPeriod(o.period_start_date, o.period_end_date)}
                                                dueDate={fmtDate(o.due_date)}
                                                status={o.status}
                                                receivedDate={fmtDate(o.received_date ?? null)}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Desktop: table */}
                        <div className="hidden md:block">
                            <Table aria-label="Final obligations">
                                <Table.Header columns={finalColumns}>
                                    {(column) => (
                                        <Table.Head
                                            id={column.id}
                                            label={column.label}
                                            isRowHeader={column.id === 'period'} // Add this line
                                        />
                                    )}
                                </Table.Header>

                                <Table.Body
                                    items={finalRows.map((o) => ({
                                        ...o,
                                        id: `${o.project_id}:${o.period_start_date}:${o.period_end_date}`,
                                    }))}
                                >
                                    {(row) => (
                                        <Table.Row id={row.id} columns={finalColumns}>
                                            {(column) => {
                                                switch (column.id) {
                                                    case "period":
                                                        return <Table.Cell>{fmtPeriod(row.period_start_date, row.period_end_date)}</Table.Cell>;
                                                    case "due":
                                                        return <Table.Cell>{fmtDate(row.due_date)}</Table.Cell>;
                                                    case "status":
                                                        return (
                                                            <Table.Cell>
                                                                <StatusPill status={row.status} dueDate={row.due_date} />
                                                            </Table.Cell>
                                                        );
                                                    case "received":
                                                        return <Table.Cell>{fmtDate(row.received_date ?? null)}</Table.Cell>;
                                                    default:
                                                        return <Table.Cell>—</Table.Cell>;
                                                }
                                            }}
                                        </Table.Row>
                                    )}
                                </Table.Body>
                            </Table>
                        </div>
                    </>
                )}
            </TableCard.Root>
        </div>
    );
}

export default ObligationsTable;